import { Link, useNavigate } from "react-router-dom";
import { Card, CardContent } from "@/components/ui/card";
import { ToggleGroup, ToggleGroupItem } from "@/components/ui/toggle-group";
import { useEmployeeKpis } from "@/hooks/use-employee-kpis";
import { useSellerProjectBreakdown } from "@/hooks/use-seller-project-breakdown";
import type { EmployeeProfileProject } from "@/hooks/use-employee-profile";
import { projectBucket, projectStageLabel } from "@/lib/employee-card";
import { formatHourlyRate, formatHours, formatNOK, formatPct } from "@/lib/format";
import { deviationStatus, TONE_TEXT } from "@/lib/widget-status";
import { cn } from "@/lib/utils";
import { ProjectCodeLabel } from "./ProjectCodeLabel";

export type ProjectFilter = "active" | "completed" | "internal" | "seller";

interface ProjectsTabProps {
  userId: string;
  projects: EmployeeProfileProject[];
  /** Admin ser solgt/loggført, avsluttede med pengetall og salgseier-lista. */
  isAdmin: boolean;
  filter: ProjectFilter;
  onFilterChange: (filter: ProjectFilter) => void;
}

interface Row {
  id: string;
  code: string | null;
  name: string;
  cells: React.ReactNode[];
}

const th = "px-3 py-2 text-xs font-medium uppercase tracking-wider text-muted-foreground";
const td = "px-3 py-2.5";

export function ProjectsTab({ userId, projects, isAdmin, filter, onFilterChange }: ProjectsTabProps) {
  const navigate = useNavigate();
  const { data: kpis } = useEmployeeKpis(isAdmin ? userId : null);
  const { data: sellerRows = [] } = useSellerProjectBreakdown(isAdmin ? userId : null);

  const byBucket = {
    active: projects.filter((p) => projectBucket(p) === "active"),
    completed: projects.filter((p) => projectBucket(p) === "completed"),
    internal: projects.filter((p) => projectBucket(p) === "internal"),
  };
  const soldById = new Map(kpis?.soldVsLogged.projects.map((r) => [r.projectId, r]));
  const completedById = new Map(kpis?.completed.projects.map((r) => [r.projectId, r]));
  const internalById = new Map(kpis?.internalTime.projects.map((r) => [r.projectId, r]));

  const filters: { value: ProjectFilter; label: string; count: number }[] = [
    { value: "active", label: "Aktive", count: byBucket.active.length },
    ...(isAdmin ? [{ value: "completed" as const, label: "Avsluttede", count: byBucket.completed.length }] : []),
    { value: "internal", label: "Interne", count: byBucket.internal.length },
    ...(isAdmin && sellerRows.length > 0
      ? [{ value: "seller" as const, label: "Som salgseier", count: sellerRows.length }]
      : []),
  ];
  const current = filters.some((f) => f.value === filter) ? filter : "active";

  const stage = (p: EmployeeProfileProject) => (
    <span className="whitespace-nowrap rounded-full bg-primary/10 px-2.5 py-0.5 text-xs font-medium text-primary">
      {projectStageLabel(p)}
    </span>
  );
  const assigned = (p: EmployeeProfileProject) => (p.budgeted_hours > 0 ? formatHours(p.budgeted_hours) : "—");

  let headers: string[];
  let rows: Row[];
  if (current === "seller") {
    headers = ["Kunde", "Budsjettavvik", "Reell timepris"];
    rows = sellerRows.map((r) => ({
      id: r.projectId,
      code: r.projectCode,
      name: r.projectName,
      cells: [
        <span className="text-muted-foreground">{r.customerName ?? "—"}</span>,
        r.budgetDeviationPct == null ? "—" : (
          <span className={TONE_TEXT[deviationStatus(r.budgetDeviationPct).tone]}>
            {r.budgetDeviationPct > 0 ? "+" : ""}
            {formatPct(r.budgetDeviationPct)}
          </span>
        ),
        r.reellTimespris == null ? "—" : formatHourlyRate(r.reellTimespris),
      ],
    }));
  } else if (current === "completed") {
    headers = ["Status", "Tildelte timer", "GEEK-andel", "Reell timepris"];
    rows = byBucket.completed.map((p) => {
      const k = completedById.get(p.project_id);
      return {
        id: p.project_id,
        code: p.project_code,
        name: p.project_name,
        cells: [stage(p), assigned(p), k ? formatNOK(k.geekShare) : "—", k ? formatHourlyRate(k.actualHourlyRate) : "—"],
      };
    });
  } else if (current === "internal") {
    headers = isAdmin ? ["Tildelte timer", "Loggført i år"] : ["Tildelte timer"];
    rows = byBucket.internal.map((p) => ({
      id: p.project_id,
      code: p.project_code,
      name: p.project_name,
      cells: isAdmin ? [assigned(p), formatHours(internalById.get(p.project_id)?.hours ?? 0)] : [assigned(p)],
    }));
  } else if (isAdmin) {
    headers = ["Fase", "Solgte timer", "Loggførte timer", "Solgt / loggført"];
    rows = byBucket.active.map((p) => {
      const s = soldById.get(p.project_id);
      return {
        id: p.project_id,
        code: p.project_code,
        name: p.project_name,
        cells: [
          stage(p),
          formatHours(s?.soldHours ?? p.budgeted_hours),
          formatHours(s?.loggedHours ?? 0),
          s?.ratioPct == null ? "—" : formatPct(s.ratioPct),
        ],
      };
    });
  } else {
    headers = ["Fase", "Tildelte timer"];
    rows = byBucket.active.map((p) => ({
      id: p.project_id,
      code: p.project_code,
      name: p.project_name,
      cells: [stage(p), assigned(p)],
    }));
  }

  return (
    <div className="space-y-3">
      <ToggleGroup
        type="single"
        value={current}
        onValueChange={(v) => v && onFilterChange(v as ProjectFilter)}
        aria-label="Prosjektstatus"
        className="justify-start"
      >
        {filters.map((f) => (
          <ToggleGroupItem key={f.value} value={f.value} className="h-8 gap-1.5 border border-border px-3 text-xs data-[state=on]:bg-foreground data-[state=on]:text-background">
            {f.label}
            <span className="tabular-nums opacity-70">{f.count}</span>
          </ToggleGroupItem>
        ))}
      </ToggleGroup>

      <Card>
        <CardContent className="p-0">
          {rows.length === 0 ? (
            <p className="px-5 py-8 text-center text-sm text-muted-foreground">Ingen prosjekter her.</p>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-sm" aria-label="Prosjekter">
                <thead>
                  <tr className="border-b border-border">
                    <th className={cn(th, "pl-5 text-left")}>Prosjekt</th>
                    {headers.map((h, i) => (
                      <th key={h} className={cn(th, i === headers.length - 1 && "pr-5", "text-right")}>{h}</th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {rows.map((r) => (
                    <tr
                      key={r.id}
                      onClick={() => navigate(`/prosjekter/${r.id}`)}
                      className="cursor-pointer border-b border-border/50 transition-colors last:border-0 hover:bg-muted/30"
                    >
                      <td className={cn(td, "pl-5")}>
                        <Link
                          to={`/prosjekter/${r.id}`}
                          onClick={(e) => e.stopPropagation()}
                          className="font-medium text-primary hover:underline"
                        >
                          <ProjectCodeLabel code={r.code} name={r.name} />
                        </Link>
                      </td>
                      {r.cells.map((c, i) => (
                        <td key={i} className={cn(td, "text-right tabular-nums", i === r.cells.length - 1 && "pr-5")}>
                          {c}
                        </td>
                      ))}
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
